import ClientCard from './card';
import { ClientProps } from '@/types';
import { Empty } from './empty/Empty';
import ModalBoleto from './boleto-modal';
import { Loading, ModalCreditLimit } from '@/components';
import { useState, useMemo, useCallback, useEffect } from 'react';

type Props = {
  showProgress?: boolean;
  clients: ClientProps[];
  selectedClient?: ClientProps;
  setLocalClient: (value: ClientProps | undefined) => void;
  isModalOpen?: boolean;
  isLoading?: boolean;
};

const DEFAULT_VISIBLE_COUNT = 6;

type ModalState = { type: 'boleto'; client: ClientProps } | { type: 'credit'; client: ClientProps } | null;

const ListClients = ({ clients, showProgress, selectedClient, setLocalClient, isLoading }: Props) => {
  const [modal, setModal] = useState<ModalState>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const localClients = useMemo(() => {
    const arr = [...(clients || [])];
    return arr.sort((a, b) => {
      if (a.ST_COMPRA === 'I' && b.ST_COMPRA !== 'I') return 1;
      if (a.ST_COMPRA !== 'I' && b.ST_COMPRA === 'I') return -1;
      return a.NM_CLIENTE?.localeCompare(b.NM_CLIENTE) || 0;
    });
  }, [clients]);

  const [visibleCount, setVisibleCount] = useState(() => {
    if (selectedClient?.COD_CLIENTE) {
      const selectedClientIndex = localClients.findIndex((c) => c.COD_CLIENTE === selectedClient?.COD_CLIENTE) + 1;
      return Math.max(selectedClientIndex, DEFAULT_VISIBLE_COUNT);
    }
    return DEFAULT_VISIBLE_COUNT;
  });

  const list = useMemo(() => {
    return localClients.slice(0, visibleCount);
  }, [localClients, visibleCount]);

  const toggleItemExpansion = (clientId: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(clientId) ? next.delete(clientId) : next.add(clientId);
      return next;
    });
  };

  const handleOpenBoleto = (c: ClientProps) => {
    setModal({ type: 'boleto', client: c });
  };

  const handleOpenCreditLimit = (c: ClientProps) => {
    setModal({ type: 'credit', client: c });
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;
      if (scrollPosition > totalHeight - 100) {
        setVisibleCount((prev) => Math.min(prev + 2, localClients.length));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [localClients]);

  useEffect(() => {
    if (
      localClients?.length === 0 ||
      !selectedClient?.COD_CLIENTE ||
      !localClients.some((c) => c.COD_CLIENTE === selectedClient?.COD_CLIENTE)
    ) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const listCompaniesContent = document.getElementById('list-companies-content') as HTMLDivElement;

    const scrollToSelectedClient = () => {
      const selectedClientElement = document.getElementById(`client-${selectedClient?.COD_CLIENTE}`) as HTMLElement;

      if (listCompaniesContent && selectedClientElement) {
        const containerRect = listCompaniesContent.getBoundingClientRect();
        const elementRect = selectedClientElement.getBoundingClientRect();
        const distanceToTop = elementRect.top - containerRect.top + listCompaniesContent.scrollTop - 20;

        window.scrollTo({ top: distanceToTop, behavior: 'instant' });
      }
    };

    setTimeout(scrollToSelectedClient, 400);
  }, [localClients?.length]);

  return (
    <>
      {!localClients.length && !isLoading ? (
        <Empty />
      ) : (
        <>
          {list.map((client, index) => {
            const isActive = selectedClient?.COD_CLIENTE === client.COD_CLIENTE;
            return (
              <ClientCard
                index={index}
                key={client.COD_CLIENTE}
                client={client}
                isChecked={isActive}
                setClient={setLocalClient}
                showProgress={showProgress}
                isExpanded={expandedItems.has(client.COD_CLIENTE)}
                toggleExpansion={() => toggleItemExpansion(client.COD_CLIENTE)}
                onOpenBoleto={handleOpenBoleto}
                onOpenCreditLimit={handleOpenCreditLimit}
              />
            );
          })}

          {visibleCount < localClients.length && <Loading />}
        </>
      )}

      {/* ⬇️ Modais fora da lista (via Portal no componente Modal) */}
      {modal?.type === 'boleto' && <ModalBoleto client={modal.client} isOpen closeModal={() => setModal(null)} />}
      {modal?.type === 'credit' && <ModalCreditLimit isOpen closeModal={() => setModal(null)} />}
    </>
  );
};

export default ListClients;
